import React, { useState, useEffect, useRef } from "react";
import { Dialog } from "primereact/dialog";
import { Dropdown, DropdownChangeEvent, DropdownFilterEvent } from "primereact/dropdown";
import { InputTextarea } from "primereact/inputtextarea";
import { Button } from "primereact/button";
import { FileUpload } from "primereact/fileupload";
import { Toast } from "primereact/toast";
import { ProgressSpinner } from "primereact/progressspinner";
import ComplaintService from "../../services/complaint/ComplaintService";
import orderServices from "../../services/orders/orderservice";
import {
  ComplaintType,
  InvoiceNoDetails,
  ProductDetails,
  CreateComplaintRequestDTO,
} from "../../models/complaints/complaint-model";
import { userOption } from "../../models/orders/order-list-model";
import "./RegisterComplaintDialog.css";

interface RegisterComplaintDialogProps {
  visible: boolean;
  onHide: () => void;
  onSuccess?: () => void;
}

enum ComplaintTypeEnum {
  Internal = 1,
  Product = 2,
  Service = 3,
}

const RegisterComplaintDialog: React.FC<RegisterComplaintDialogProps> = ({
  visible,
  onHide,
  onSuccess,
}) => {
  const toast = useRef<Toast>(null);
  const fileUploadRef = useRef<any>(null);
  const debounceTimeout = useRef<NodeJS.Timeout | null>(null);
  const blankUserOption = [{ userID: 0, detail: "", name: "", primaryContactNbr: "" }];

  // State management - Form fields
  const [complaintTypeId, setComplaintTypeId] = useState<number | null>(null);
  const [customerId, setCustomerId] = useState<number | null>(null);
  const [invoiceId, setInvoiceId] = useState<number | null>(null);
  const [orderDetailId, setOrderDetailId] = useState<number | null>(null);
  const [description, setDescription] = useState<string>("");
  const [priority, setPriority] = useState<string | null>(null);
  const [attachments, setAttachments] = useState<File[]>([]);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  // State management - Dropdowns
  const [complaintTypes, setComplaintTypes] = useState<ComplaintType[]>([]);
  const [orderUsers, setOrderUsers] = useState<any[]>([]);
  const [invoices, setInvoices] = useState<InvoiceNoDetails[]>([]);
  const [products, setProducts] = useState<ProductDetails[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [loginUser, setLoginUser] = useState<any>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isInvoiceLoading, setIsInvoiceLoading] = useState(false);
  const [isProductLoading, setIsProductLoading] = useState(false);

  const priorityOptions = [
    { name: "High", code: "High" },
    { name: "Medium", code: "Medium" },
    { name: "Low", code: "Low" },
  ];

  // Effects
  useEffect(() => {
    if (visible) {
      resetForm();
      loadInitialData();
    }
  }, [visible]);

  useEffect(() => {
    const loginData = localStorage.getItem("user");
    if (loginData) {
      setLoginUser(JSON.parse(loginData));
    }
  }, []);

  // Load complaint types on component mount
  const loadInitialData = async () => {
    try {
      setIsLoading(true);
      const types = await ComplaintService.getComplaintTypes();
      setComplaintTypes(types);
    } catch (error) {
      console.error("Error loading complaint types:", error);
      showMessage("Error", "Failed to load complaint types", "error");
    } finally {
      setIsLoading(false);
    }
  };

  // Clear individual field error
  const clearFieldError = (fieldName: string) => {
    if (errors[fieldName]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[fieldName];
        return newErrors;
      });
    }
  };

  // Validation
  const validateForm = (): boolean => {
    const newErrors: { [key: string]: string } = {};

    if (!complaintTypeId) newErrors.complaintTypeId = "Complaint type is required";
    if (!description || description.length < 10) {
      newErrors.description = "Description is required and must be at least 10 characters";
    }
    if (!priority) newErrors.priority = "Priority is required";

    // Type-specific validation
    if (
      complaintTypeId === ComplaintTypeEnum.Product ||
      complaintTypeId === ComplaintTypeEnum.Service
    ) {
      if (!customerId) newErrors.customerId = "Customer is required";
    }

    if (complaintTypeId === ComplaintTypeEnum.Product) {
      if (!invoiceId) newErrors.invoiceId = "Invoice is required";
      if (!orderDetailId) newErrors.orderDetailId = "Product is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle complaint type change
  const handleComplaintTypeChange = (e: DropdownChangeEvent) => {
    const typeId = e.value;
    setComplaintTypeId(typeId);
    clearFieldError("complaintTypeId");

    // Reset dependent fields when complaint type changes
    setCustomerId(null);
    setInvoiceId(null);
    setOrderDetailId(null);
    setOrderUsers(blankUserOption);
    setInvoices([]);
    setProducts([]);
  };

  // Handle user dropdown change
  const handleUserChange = async (e: DropdownChangeEvent) => {
    const userId = e.value;
    setCustomerId(userId);
    clearFieldError("customerId");

    // Reset dependent fields
    setInvoiceId(null);
    setOrderDetailId(null);
    setInvoices([]);
    setProducts([]);

    // Load invoices for selected user
    if (userId && userId !== 0) {
      try {
        setIsInvoiceLoading(true);
        const userInvoices = await ComplaintService.getUserInvoicesFilterable(userId);
        setInvoices(userInvoices);
      } catch (error) {
        console.error("Error loading invoices:", error);
        showMessage("Error", "Failed to load invoices", "error");
      } finally {
        setIsInvoiceLoading(false);
      }
    }
  };

  // Handle user dropdown filter
  const handleUserFilter = (e: DropdownFilterEvent) => {
    const query = e.filter;

    if (debounceTimeout.current) {
      clearTimeout(debounceTimeout.current);
    }

    debounceTimeout.current = setTimeout(() => {
      if (query && query.length > 0) {
        orderServices
          .getOrderUsers(query)
          .then((response: userOption[]) => {
            if (response && response.length > 0) {
              const userOptions = response.map((user) => ({
                userID: user.userID,
                detail: `${user.name}, ${user.primaryContactNbr}`,
              }));
              setOrderUsers(userOptions);
            } else {
              setOrderUsers(blankUserOption);
            }
          })
          .catch((error) => {
            console.error("Error fetching users:", error);
            setOrderUsers(blankUserOption);
          });
      }
    }, 300);
  };

  // Handle invoice change
  const handleInvoiceChange = async (e: DropdownChangeEvent) => {
    const selectedInvoiceId = e.value;
    setInvoiceId(selectedInvoiceId);
    clearFieldError("invoiceId");

    // Reset product field
    setOrderDetailId(null);
    setProducts([]);

    // Load products for selected invoice
    if (selectedInvoiceId) {
      try {
        setIsProductLoading(true);
        const invoiceDetails = invoices.find(
          (inv) => inv.orderInvoiceID === selectedInvoiceId
        );
        if (invoiceDetails) {
          const productList = await ComplaintService.getInvoiceProductDetails(
            invoiceDetails.orderInvoiceID
          );
          setProducts(productList);
        }
      } catch (error) {
        console.error("Error loading products:", error);
        showMessage("Error", "Failed to load products", "error");
      } finally {
        setIsProductLoading(false);
      }
    }
  };

  // Handle file selection
  const handleFileSelect = (e: any) => {
    const files = Array.from(e.files || []) as File[];
    setAttachments(files);
  };

  // Handle file removal
  const handleFileRemove = (e: any) => {
    const remainingFiles = attachments.filter(
      (file) => file.name !== e.file.name || file.size !== e.file.size
    );
    setAttachments(remainingFiles);
  };

  // Handle upload clear
  const handleUploadClear = () => {
    setAttachments([]);
  };

  // Handle submit
  const handleSubmitComplaint = async () => {
    if (!validateForm()) {
      showMessage("Error", "Please fill all required fields", "error");
      return;
    }

    try {
      setIsSubmitting(true);

      const payload: CreateComplaintRequestDTO = {
        complaintTypeId: complaintTypeId || 0,
        description: description,
        priority: priority || "",
      };

      // Add type-specific fields
      if (complaintTypeId === ComplaintTypeEnum.Product) {
        payload.customerId = customerId || 0;
        payload.invoiceId = invoiceId || 0;
        payload.orderDetailId = orderDetailId || 0;
      } else if (complaintTypeId === ComplaintTypeEnum.Service) {
        payload.customerId = customerId || 0;
      }

      const complaintData = {
        ...payload,
        attachments: attachments,
      };

      const response = await ComplaintService.createComplaintV2(complaintData);

      if (response.isSuccess) {
        resetForm();
        onHide();
        if (onSuccess) {
          onSuccess();
        }
      } else {
        showMessage(
          "Error",
          response.message || "Failed to create complaint",
          "error"
        );
      }
    } catch (error: any) {
      console.error("Error submitting complaint:", error);
      showMessage(
        "Error",
        error?.message || "An error occurred while submitting the complaint",
        "error"
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  // Reset form
  const resetForm = () => {
    setComplaintTypeId(null);
    setCustomerId(null);
    setInvoiceId(null);
    setOrderDetailId(null);
    setDescription("");
    setPriority(null);
    setAttachments([]);
    setErrors({});
    setOrderUsers(blankUserOption);
    setInvoices([]);
    setProducts([]);
    if (fileUploadRef.current) {
      fileUploadRef.current.clear();
    }
  };

  // Show toast message
  const showMessage = (severity: string, summary: string, type: any) => {
    toast.current?.show({
      severity: type,
      summary: severity,
      detail: summary,
      life: 3000,
    });
  };

  // ── Invoice display templates — uses createdDate (field returned by API) ──
  const invoiceItemTemplate = (option: InvoiceNoDetails) => {
    // API returns createdDate (e.g. "2024-09-06T07:02:43.577"), not invoiceDate
    const rawDate = (option as any)?.createdDate ?? option?.invoiceDate;
    const dateStr = rawDate
      ? new Date(rawDate).toLocaleDateString("en-IN", {
          day: "2-digit",
          month: "short",
          year: "numeric",
        })
      : "";
    return (
      <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
        <span style={{ fontWeight: 500 }}>{option?.invoiceNo}</span>
        {dateStr && <span style={{ color: "#6b7280" }}>-</span>}
        {dateStr && (
          <span style={{ color: "#6b7280", fontSize: "0.85rem" }}>{dateStr}</span>
        )}
      </div>
    );
  };

  const invoiceValueTemplate = (option?: InvoiceNoDetails) => {
    if (!option) return <span>Select Invoice</span>;
    // API returns createdDate (e.g. "2024-09-06T07:02:43.577"), not invoiceDate
    const rawDate = (option as any)?.createdDate ?? option?.invoiceDate;
    const dateStr = rawDate
      ? new Date(rawDate).toLocaleDateString("en-IN", {
          day: "2-digit",
          month: "short",
          year: "numeric",
        })
      : "";
    return (
      <div style={{ display: "flex", alignItems: "center", gap: "0.4rem" }}>
        <span style={{ fontWeight: 500 }}>{option.invoiceNo}</span>
        {dateStr && <span style={{ color: "#6b7280" }}>-</span>}
        {dateStr && (
          <span style={{ color: "#6b7280", fontSize: "0.85rem" }}>{dateStr}</span>
        )}
      </div>
    );
  };
  // ── END invoice templates ──────────────────────────────────────────────────

  const renderComplaintTypeContent = () => {
    return (
      <div className="complaint-fields">

        {/* Conditional fields for Product Complaint */}
        {complaintTypeId === ComplaintTypeEnum.Product && (
          <>
            <div className="field px-3">
              <label htmlFor="customerId" className="required-label">
                User <span style={{ color: "red", fontSize: "18px" }}>*</span>
              </label>
              <Dropdown
                id="customerId"
                value={customerId}
                onChange={handleUserChange}
                onBlur={() => clearFieldError("customerId")}
                onFilter={handleUserFilter}
                options={orderUsers}
                optionLabel="detail"
                optionValue="userID"
                placeholder="Search for User/Mobile"
                filter
                filterBy="detail"
                className={`w-full ${errors.customerId ? "p-invalid" : ""}`}
                panelClassName="complaint-dropdown-panel"
              />
              {errors.customerId && (
                <small className="p-error">{errors.customerId}</small>
              )}
            </div>

            <div className="field px-3">
              <label htmlFor="invoiceId" className="required-label">
                Invoice Number{" "}
                <span style={{ color: "red", fontSize: "18px" }}>*</span>
              </label>
              <Dropdown
                id="invoiceId"
                value={invoiceId}
                onChange={handleInvoiceChange}
                onBlur={() => clearFieldError("invoiceId")}
                options={invoices}
                filter
                optionLabel="invoiceNo"
                optionValue="orderInvoiceID"
                placeholder="Select Invoice"
                className={`w-full ${errors.invoiceId ? "p-invalid" : ""}`}
                panelClassName="complaint-dropdown-panel"
                itemTemplate={invoiceItemTemplate}
                valueTemplate={invoiceValueTemplate}
              />
              {errors.invoiceId && (
                <small className="p-error">{errors.invoiceId}</small>
              )}
            </div>

            <div className="field px-3">
              <label htmlFor="orderDetailId" className="required-label">
                Product Name{" "}
                <span style={{ color: "red", fontSize: "18px" }}>*</span>
              </label>
              <Dropdown
                id="orderDetailId"
                value={orderDetailId}
                onChange={(e) => {
                  setOrderDetailId(e.value);
                  clearFieldError("orderDetailId");
                }}
                onBlur={() => clearFieldError("orderDetailId")}
                options={products}
                optionLabel="productName"
                optionValue="orderDetailID"
                placeholder="Select Product"
                className={`w-full ${errors.orderDetailId ? "p-invalid" : ""}`}
              />
              {errors.orderDetailId && (
                <small className="p-error">{errors.orderDetailId}</small>
              )}
            </div>
          </>
        )}

        {/* Conditional fields for Service Complaint */}
        {complaintTypeId === ComplaintTypeEnum.Service && (
          <div className="field px-3">
            <label htmlFor="customerId" className="required-label">
              User <span style={{ color: "red", fontSize: "18px" }}>*</span>
            </label>
            <Dropdown
              id="customerId"
              value={customerId}
              onChange={handleUserChange}
              onBlur={() => clearFieldError("customerId")}
              onFilter={handleUserFilter}
              options={orderUsers}
              optionLabel="detail"
              optionValue="userID"
              placeholder="Search for User/Mobile"
              filter
              filterBy="detail"
              className={`w-full ${errors.customerId ? "p-invalid" : ""}`}
              panelClassName="complaint-dropdown-panel"
            />
            {errors.customerId && (
              <small className="p-error">{errors.customerId}</small>
            )}
          </div>
        )}

        {/* Description */}
        <div className="field px-3">
          <label htmlFor="description" className="required-label">
            Description <span style={{ color: "red", fontSize: "18px" }}>*</span>
          </label>
          <InputTextarea
            id="description"
            value={description}
            onChange={(e) => {
              setDescription(e.target.value);
              clearFieldError("description");
            }}
            onBlur={() => clearFieldError("description")}
            rows={4}
            className={`w-full ${errors.description ? "p-invalid" : ""}`}
            placeholder="Enter complaint description (minimum 10 characters)"
          />
          {errors.description && (
            <small className="p-error">{errors.description}</small>
          )}
        </div>

        {/* Priority */}
        <div className="field px-3">
          <label htmlFor="priority" className="required-label">
            Priority <span style={{ color: "red", fontSize: "18px" }}>*</span>
          </label>
          <Dropdown
            id="priority"
            value={priority}
            onChange={(e) => {
              setPriority(e.value);
              clearFieldError("priority");
            }}
            onBlur={() => clearFieldError("priority")}
            options={priorityOptions}
            optionLabel="name"
            optionValue="code"
            placeholder="Select Priority"
            className={`w-full ${errors.priority ? "p-invalid" : ""}`}
            panelClassName="complaint-dropdown-panel"
          />
          {errors.priority && (
            <small className="p-error">{errors.priority}</small>
          )}
        </div>

        {/* File upload - common for all types */}
        <div className="field px-3">
          <label htmlFor="attachments">Attachment (Documents/Images)</label>
          <FileUpload
            ref={fileUploadRef}
            name="attachments"
            accept="image/*,.pdf,.doc,.docx"
            maxFileSize={5000000}
            multiple
            auto={false}
            customUpload
            onSelect={handleFileSelect}
            onRemove={handleFileRemove}
            onClear={handleUploadClear}
            emptyTemplate={<p className="m-0">Drag and drop files here to upload.</p>}
            chooseOptions={{
              icon: "pi pi-fw pi-plus",
              label: "Choose",
              className: "p-button-success",
            }}
            uploadOptions={{ className: "hidden-button" }}
            cancelOptions={{ className: "hidden-button" }}
          />
        </div>
      </div>
    );
  };

  const footer = (
    <div className="complaint-dialog-footer">
      <Button
        label="Reset"
        icon="pi pi-times"
        onClick={resetForm}
        className="p-button-text"
        disabled={isSubmitting}
      />
      <Button
        label="Register Complaint"
        icon="pi pi-check"
        onClick={handleSubmitComplaint}
        loading={isSubmitting}
        disabled={isSubmitting || !complaintTypeId}
      />
    </div>
  );

  return (
    <>
      <Toast ref={toast} />
      <Dialog
        visible={visible}
        onHide={onHide}
        header="Register Complaint"
        modal
        className="complaint-dialog"
        style={{ width: "50vw" }}
        footer={footer}
        contentClassName="complaint-dialog-content-fix"
        contentStyle={{ padding: 0 }}
      >
        <div className="complaint-custom-scroll" style={{ padding: "1.5rem 2rem" }}>
          {isLoading && !complaintTypes.length ? (
            <div className="loading-container">
              <ProgressSpinner />
              <p>Loading...</p>
            </div>
          ) : (
            <div className="complaint-form">

              {/* Complaint Type Selection */}
              <div className="field px-3 floating-field">
                <span className="floating-label">
                  Complaint Type <span className="required">*</span>
                </span>

                <Dropdown
                  id="complaintTypeId"
                  value={complaintTypeId}
                  onChange={handleComplaintTypeChange}
                  options={complaintTypes}
                  optionLabel="name"
                  optionValue="id"
                  placeholder="Select Complaint Type"
                  className={`w-full ${errors.complaintTypeId ? "p-invalid" : ""}`}
                  panelClassName="complaint-dropdown-panel"
                />

                {errors.complaintTypeId && (
                  <small className="p-error">{errors.complaintTypeId}</small>
                )}
              </div>

              {/* Dynamic fields based on complaint type */}
              {complaintTypeId && renderComplaintTypeContent()}
            </div>
          )}
        </div>
      </Dialog>
    </>
  );
};

export default RegisterComplaintDialog;